import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/Login.css"; // نفس ستايل اللوجين
import { confirmEmailOTP, resendEmailOTP } from "../../services/authService";
import { AuthContext } from "../../context/AuthContext";

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "user@example.com";
  const { user } = useContext(AuthContext);

  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timeLeft, setTimeLeft] = useState(60);
  const [resending, setResending] = useState(false);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === 0) return;
    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Handle OTP input
  const handleChange = (element, index) => {
    if (isNaN(element.value)) return;
    let newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);
    if (element.value !== "" && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  // Verify OTP
  const handleVerify = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    const code = otp.join("");
    if (code.length < 6) {
      setError(
        "برجاء إدخال الكود المكون من 6 أرقام | Please enter the 6-digit code.",
      );
      setLoading(false);
      return;
    }

    try {
      const response = await confirmEmailOTP(email, code);
      if (response.message === "Email confirmed successfully.") {
        setSuccess(
          "تم تأكيد الحساب بنجاح! جاري التوجيه... | Email confirmed successfully!",
        );
        setTimeout(async () => {
          setLoading(false);
          if (user) {
            window.location.reload();
            navigate("/");
          } else {
            navigate("/auth/login");
          }
        }, 1500);
      } else {
        setSuccess(response.message);
      }
    } catch (error) {
      setError(
        error?.message ||
          "فشل التحقق. حاول مرة أخرى | Verification failed. Please try again.",
      );
    } finally {
      if (!success) setLoading(false);
    }
  };

  // Resend code
  const handleResend = async () => {
    setResending(true);
    setError("");
    setSuccess("");
    setTimeLeft(60);
    try {
      const response = await resendEmailOTP(email);
      setSuccess(
        response.message || "تم إعادة إرسال الكود | OTP resent successfully!",
      );
    } catch (error) {
      setError(error.message || "فشل إعادة الإرسال | Failed to resend OTP");
    }
    await new Promise((res) => setTimeout(res, 1000));
    setResending(false);
  };

  return (
    <>
      <div className="background-wrapper">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
      </div>

      <div
        className="login-advanced-container"
        style={{ paddingTop: "100px", paddingBottom: "50px" }}
      >
        <Container fluid="md">
          <Row className="justify-content-center m-0">
            <Col xs={12} md={8} lg={6} className="p-0">
              <div
                className="login-card shadow-lg p-4 p-md-5 d-flex flex-column align-items-center"
                style={{
                  background: "rgba(15, 23, 42, 0.7)",
                  backdropFilter: "blur(20px)",
                }}
              >
                {/* Logo */}
                <img
                  src="/CampusPulsLogo.png"
                  alt="Logo"
                  style={{ width: "90px", marginBottom: "1.5rem" }}
                />

                {/* Header Section */}
                <div className="text-center mb-4 w-100">
                  <div className="d-flex flex-column gap-1 mb-3">
                    <h3 className="fw-bold text-white mb-0" dir="rtl">
                      تأكيد البريد الإلكتروني
                    </h3>
                    <h4
                      className="fw-bold mb-0"
                      dir="ltr"
                      style={{ fontSize: "1.2rem", color: "#cbd5e1" }}
                    >
                      Verify Your Email
                    </h4>
                  </div>
                  <div
                    className="d-flex flex-column gap-2 p-3 rounded-3 border border-white border-opacity-10"
                    style={{ background: "rgba(255,255,255,0.03)" }}
                  >
                    <p
                      className="small m-0"
                      style={{ color: "#f8fafc" }}
                      dir="rtl"
                    >
                      لقد أرسلنا كود التحقق المكون من 6 أرقام إلى:
                    </p>
                    <p
                      className="small m-0"
                      style={{ color: "#94a3b8" }}
                      dir="ltr"
                    >
                      We sent a 6-digit verification code to:
                    </p>
                    {/* عرض الإيميل كامل بدون إخفاء */}
                    <strong
                      className="text-info mt-1"
                      dir="ltr"
                      style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}
                    >
                      {email}
                    </strong>
                  </div>
                </div>

                {/* Global Alerts */}
                {error && (
                  <div
                    className="alert alert-danger bg-danger bg-opacity-25 text-white border-0 text-center w-100 mb-4"
                    style={{ borderRadius: "12px" }}
                  >
                    {error}
                  </div>
                )}
                {success && (
                  <div
                    className="alert alert-success bg-success bg-opacity-25 text-white border-0 text-center w-100 mb-4"
                    style={{ borderRadius: "12px" }}
                  >
                    {success}
                  </div>
                )}

                {/* OTP Inputs */}
                <div
                  className="d-flex justify-content-center gap-2 gap-sm-3 mb-4 w-100"
                  dir="ltr"
                >
                  {otp.map((data, index) => (
                    <input
                      type="text"
                      maxLength="1"
                      key={index}
                      value={data}
                      onChange={(e) => handleChange(e.target, index)}
                      className="advanced-input text-center fs-4 fw-bold p-0 m-0"
                      style={{
                        width: "clamp(40px, 12vw, 55px)",
                        height: "clamp(50px, 15vw, 65px)",
                        borderRadius: "12px",
                        caretColor: "transparent", // لإخفاء مؤشر الكتابة ليكون شكلها أنظف
                      }}
                      disabled={loading}
                    />
                  ))}
                </div>

                {/* Verify Button */}
                <button
                  onClick={handleVerify}
                  className="btn-primary-advanced w-100 d-flex flex-column align-items-center justify-content-center gap-1 py-3 mb-4"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="d-flex align-items-center gap-2">
                      <Spinner as="span" animation="border" size="sm" />
                      <span className="fw-bold">
                        جاري التأكيد... | Verifying...
                      </span>
                    </div>
                  ) : (
                    <>
                      <span style={{ fontWeight: "700", fontSize: "1.1rem" }}>
                        تأكيد الحساب
                      </span>
                      <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>
                        Verify Email
                      </span>
                    </>
                  )}
                </button>

                {/* Resend Section */}
                <div className="text-center d-flex flex-column align-items-center gap-2 mt-2 w-100">
                  {timeLeft > 0 ? (
                    <>
                      <small style={{ color: "#cbd5e1" }} dir="rtl">
                        يمكنك إعادة الإرسال خلال {timeLeft} ثانية
                      </small>
                      <small style={{ color: "#94a3b8" }} dir="ltr">
                        You can resend the code in {timeLeft}s
                      </small>
                    </>
                  ) : (
                    <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-2">
                      <span className="small" style={{ color: "#cbd5e1" }}>
                        لم تستلم الكود؟ | Didn't receive code?
                      </span>
                      <button
                        onClick={handleResend}
                        className="btn btn-link text-decoration-none p-0 fw-bold m-0"
                        disabled={resending}
                        style={{ color: "#6366f1", fontSize: "0.95rem" }}
                      >
                        {resending
                          ? "جاري الإرسال... | Resending..."
                          : "إعادة إرسال الكود | Resend Code"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default EmailVerification;

// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import "bootstrap/dist/css/bootstrap.min.css";
// import { confirmEmailOTP, resendEmailOTP } from "../../services/authService";
// import { AuthContext } from "../../context/AuthContext";

// const EmailVerification = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const email = location.state?.email || "user@example.com";
//   const { user } = useContext(AuthContext);

//   // Mask part of the email
//   const maskEmail = (email) => {
//     const [user, domain] = email.split("@");
//     const maskedUser =
//       user?.length <= 2
//         ? user[0] + "*".repeat(user?.length - 1)
//         : user?.slice(0, 2) + "*".repeat(user?.length - 2);
//     return `${maskedUser}@${domain}`;
//   };

//   const [otp, setOtp] = useState(Array(6).fill(""));
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState("");
//   const [timeLeft, setTimeLeft] = useState(60);
//   const [resending, setResending] = useState(false);

//   //  Timer countdown
//   useEffect(() => {
//     if (timeLeft === 0) return;
//     const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
//     return () => clearTimeout(timer);
//   }, [timeLeft]);

//   // Handle OTP input
//   const handleChange = (element, index) => {
//     if (isNaN(element.value)) return;
//     let newOtp = [...otp];
//     newOtp[index] = element.value;
//     setOtp(newOtp);
//     if (element.value !== "" && element.nextSibling) {
//       element.nextSibling.focus();
//     }
//   };

//   // Verify OTP (Mock API)
//   const handleVerify = async () => {
//     setLoading(true);
//     setError("");
//     setSuccess("");

//     const code = otp.join("");
//     if (code.length < 6) {
//       setError("Please enter the 6-digit code.");
//       setLoading(false);
//       return;
//     }

//     try {
//       const response = await confirmEmailOTP(email, code);
//       if (response.message === "Email confirmed successfully.") {
//         setSuccess(response.message);
//         setTimeout(async () => {
//           setLoading(false);
//           if (user) {
//             window.location.reload();
//             navigate("/");
//           } else {
//             navigate("/auth/login");
//           }
//         }, 1500);
//       } else setSuccess(response.message);
//     } catch (error) {
//       setError(error?.message || "Verification failed. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   //  Resend code
//   const handleResend = async () => {
//     setResending(true);
//     setTimeLeft(60);
//     try {
//       const response = await resendEmailOTP(email);
//       setSuccess(response.message);
//     } catch (error) {
//       setError(error.message || "failed to resent OTP");
//     }
//     await new Promise((res) => setTimeout(res, 1000));
//     setResending(false);
//   };

//   return (
//     <div
//       className="d-flex justify-content-center align-items-center vh-100"
//       style={{
//         backgroundColor: "#f8f9fa",
//       }}
//     >
//       <div
//         className="card p-4 shadow-sm border-0 text-center"
//         style={{
//           width: "400px",
//           borderRadius: "16px",
//           backgroundColor: "white",
//         }}
//       >
//         <h4 className="fw-bold text-primary mb-3">Verify Your Email</h4>
//         <p className="text-muted mb-4">
//           We sent a 6-digit verification code to your email:
//           <br />
//           <strong className="text-dark">{maskEmail(email)}</strong>
//         </p>

//         <div className="d-flex justify-content-between mb-3">
//           {otp.map((data, index) => (
//             <input
//               type="text"
//               maxLength="1"
//               key={index}
//               value={data}
//               onChange={(e) => handleChange(e.target, index)}
//               className="form-control text-center fs-5 fw-bold"
//               style={{
//                 width: "48px",
//                 height: "55px",
//                 borderRadius: "10px",
//                 border: "2px solid #0d6efd",
//                 color: "#0d6efd",
//               }}
//             />
//           ))}
//         </div>

//         {error && <div className="text-danger mb-2">{error}</div>}
//         {success && <div className="text-success mb-2">{success}</div>}

//         <button
//           onClick={handleVerify}
//           className="btn btn-primary w-100 py-2 mt-2 fw-bold"
//           disabled={loading}
//         >
//           {loading ? (
//             <>
//               <span
//                 className="spinner-border spinner-border-sm me-2"
//                 role="status"
//               ></span>
//               Verifying...
//             </>
//           ) : (
//             "Verify Email"
//           )}
//         </button>

//         <div className="mt-3">
//           {timeLeft > 0 ? (
//             <small className="text-muted">
//               You can resend the code in {timeLeft}s
//             </small>
//           ) : (
//             <button
//               onClick={handleResend}
//               className="btn btn-link p-0 text-decoration-none text-primary fw-semibold"
//               disabled={resending}
//             >
//               {resending ? "Resending..." : "Resend Code"}
//             </button>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EmailVerification;
